import React, { useState, useMemo } from 'react';
import { InverterPerformance } from '../../services/inverterPerformanceService';
import { HealthStatusIndicator } from './HealthStatusIndicator';

interface InverterMetricsTableProps {
  inverters: InverterPerformance[];
  onExport?: (format: 'csv' | 'pdf') => void;
}

type SortField = keyof InverterPerformance;
type SortDirection = 'asc' | 'desc';

export const InverterMetricsTable: React.FC<InverterMetricsTableProps> = ({ 
  inverters, 
  onExport 
}) => {
  const [sortField, setSortField] = useState<SortField>('inverter_id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const sortedInverters = useMemo(() => {
    return [...inverters].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      const numA = Number(aVal);
      const numB = Number(bVal);
      return sortDirection === 'asc' ? numA - numB : numB - numA;
    });
  }, [inverters, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getPerformanceColor = (value: number, field: string) => {
    if (field === 'efficiency' || field === 'pr' || field === 'cuf') {
      return value < 70 
        ? 'text-red-600 dark:text-red-400' 
        : value < 85 
        ? 'text-yellow-600 dark:text-yellow-400' 
        : 'text-green-600 dark:text-green-400';
    }
    return '';
  };

  const getRowHighlight = (inverter: InverterPerformance) => {
    if (inverter.status !== 'generating') return 'bg-red-50 dark:bg-red-900/20';
    if (inverter.pr < 70 || inverter.cuf < 70) return 'bg-yellow-50 dark:bg-yellow-900/20';
    return '';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Inverter Performance Metrics</h3>
        {onExport && (
          <div className="flex gap-2">
            <button 
              onClick={() => onExport('csv')}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Export CSV
            </button>
            <button 
              onClick={() => onExport('pdf')}
              className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              Export PDF
            </button>
          </div>
        )}
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              {[
                { key: 'inverter_id', label: 'Inverter ID' },
                { key: 'specific_yield', label: 'Specific Yield (kWh/kWp)' },
                { key: 'efficiency', label: 'Efficiency (%)' },
                { key: 'pr', label: 'PR (%)' },
                { key: 'cuf', label: 'CUF (%)' },
                { key: 'status', label: 'Status' }
              ].map(({ key, label }) => (
                <th 
                  key={key}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  onClick={() => handleSort(key as SortField)}
                >
                  <div className="flex items-center gap-1">
                    {label}
                    {sortField === key && (
                      <span className="text-blue-600 dark:text-blue-400">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {sortedInverters.map((inverter, index) => (
              <tr 
                key={`${inverter.inverter_id}-${inverter.date}-${index}`} 
                className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${getRowHighlight(inverter)}`}
              >
                <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">{inverter.inverter_id}</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{inverter.specific_yield.toFixed(2)}</td>
                <td className={`px-4 py-3 font-medium ${getPerformanceColor(inverter.efficiency, 'efficiency')}`}>
                  {inverter.efficiency.toFixed(1)}
                </td>
                <td className={`px-4 py-3 font-medium ${getPerformanceColor(inverter.pr, 'pr')}`}>
                  {inverter.pr.toFixed(1)}
                </td>
                <td className={`px-4 py-3 font-medium ${getPerformanceColor(inverter.cuf, 'cuf')}`}>
                  {inverter.cuf.toFixed(1)}
                </td>
                <td className="px-4 py-3">
                  <HealthStatusIndicator 
                    status={inverter.status} 
                    healthScore={inverter.health_score}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};